/**
 * Popup Class
 *
 * Handles individual popup instances with iframe loading,
 * animations, and postMessage communication.
 *
 * Display-mode-specific behavior is delegated to DisplayModeStrategy.
 */

import type {
	PopupConfig,
	IframeMessage,
	ParentMessage,
} from '../shared/types/popup';
import { getCookie, setCookie } from './utils/cookies';
import { createLogger } from '../shared/logger';
import {
	createDisplayModeStrategy,
	type DisplayModeStrategy,
} from './display-modes';
import { createAnalyticsTracker, type AnalyticsTracker } from './analytics';
import { InlineContent, hideInitialPopup } from './inline-content';
import {
	broadcastPopupState,
	type PopupLifecycleState,
} from './triggers/trigger-state-broadcaster';

const logger = createLogger('Popup');

export class Popup {
	private _config: PopupConfig;
	private displayMode: DisplayModeStrategy;
	private container: HTMLElement | null = null;
	private backdrop: HTMLElement | null = null;
	private dialog: HTMLElement | null = null;
	private iframe: HTMLIFrameElement | null = null;
	private inlineContent: InlineContent | null = null;
	private closeButton: HTMLElement | null = null;
	private isOpen = false;
	private isClosing = false;
	private isReady = false;
	private isPendingShow = false;
	private restoreAnimationAfterInitialReveal = false;
	private boundHandleMessage: (event: MessageEvent) => void;
	private boundHandleKeydown: (event: KeyboardEvent) => void;
	private tracker: AnalyticsTracker | null = null;

	/**
	 * Get the popup config, filtered through `gpp_popup_config` at runtime.
	 */
	private get config(): PopupConfig {
		if (typeof window.gform?.applyFilters === 'function') {
			/**
			 * Filter the popup configuration before it is used at runtime.
			 *
			 * Allows dynamic modification of any popup configuration value at runtime
			 * using Gravity Forms' applyFilters.
			 *
			 * @since 1.0.5
			 *
			 * @param {Object} config               The popup configuration object
			 * @param {number} config.feedId        The popup feed ID
			 * @param {number} config.formId        The Gravity Forms form ID
			 * @param {string} config.iframeUrl     The iframe URL
			 * @param {string} config.iframeSandbox Space-separated iframe sandbox tokens
			 * @param {Object} config.trigger       Trigger settings
			 * @param {Object} config.display       Display settings
			 * @param {Object} config.behavior      Behavior settings
			 *
			 * @example Append query parameters to the iframe URL for a specific popup
			 * <code>
			 * window.gform.addFilter('gpp_popup_config', function(config) {
			 *     if (config.feedId === 37) {
			 *         var url = new URL(config.iframeUrl);
			 *         url.searchParams.set('my_param', 'value');
			 *         config.iframeUrl = url.toString();
			 *     }
			 *     return config;
			 * });
			 * </code>
			 *
			 * @example Switch display mode on mobile
			 * <code>
			 * window.gform.addFilter('gpp_popup_config', function(config) {
			 *     if (window.innerWidth < 768) {
			 *         config.display.displayMode = 'banner';
			 *     }
			 *     return config;
			 * });
			 * </code>
			 *
			 * @example Customize loader markup
			 * <code>
			 * window.gform.addFilter('gpp_popup_config', function(config) {
			 *     config.loaderHtml = '<div class="contact-form-loader">Loading contact form</div>';
			 *     return config;
			 * });
			 * </code>
			 *
			 * @example Add allow-popups-to-escape-sandbox to the iframe sandbox
			 * <code>
			 * window.gform.addFilter('gpp_popup_config', function(config) {
			 *     config.iframeSandbox += ' allow-popups-to-escape-sandbox';
			 *     return config;
			 * });
			 * </code>
			 */
			return window.gform.applyFilters('gpp_popup_config', {
				...this._config,
			});
		}

		return this._config;
	}

	constructor(config: PopupConfig) {
		this._config = config;
		this.displayMode = createDisplayModeStrategy(config.display);
		this.boundHandleMessage = this.handleMessage.bind(this);
		this.boundHandleKeydown = this.handleKeydown.bind(this);

		// Initialize analytics tracker if enabled
		this.tracker = createAnalyticsTracker();

		// Always start listening for messages so we can receive ready events from preloaded iframes
		window.addEventListener('message', this.boundHandleMessage);
	}

	/**
	 * Get the feed ID for this popup
	 */
	public getFeedId(): number {
		return this.config.feedId;
	}

	/**
	 * Check if popup is currently open
	 */
	public getIsOpen(): boolean {
		return this.isOpen;
	}

	/**
	 * Check if popup respects dismissal (remembers when user dismisses)
	 */
	public getRespectsDismisal(): boolean {
		return this.config.behavior.respectDismissal;
	}

	/**
	 * Show the popup
	 * @param bypassDismissal - If true, bypasses the dismissal cookie check (used by forceLaunch)
	 */
	public show(bypassDismissal = false): void {
		logger.group(`show() feedId: ${this.config.feedId}`, () => {
			// Check dismissal cookie (unless bypassed)
			if (
				!bypassDismissal &&
				this.config.behavior.respectDismissal &&
				this.wasDismissed()
			) {
				logger.log('Popup was dismissed, not showing');
				return;
			}

			if (this.isOpen || this.isClosing) {
				logger.log('Popup already open, not showing');
				return;
			}

			if (this.isPendingShow) {
				logger.log('Already pending show, waiting for iframe ready');
				return;
			}

			// Create popup if not already created (by preload)
			if (!this.container) {
				logger.log('Creating popup...');
				this.createPopup();
			} else {
				logger.log('Popup already exists (preloaded)');
			}

			// Re-check the iframe URL in case a filter modified it at runtime
			// (e.g. to pass field values that weren't available at preload time).
			this.syncIframeUrl();

			logger.log('Attaching event listeners...');
			this.attachEventListeners();

			// If iframe is already ready (preloaded), show immediately without loader
			if (this.isReady) {
				logger.log('Iframe already ready, showing immediately');
				this.revealPopup();
			} else {
				logger.log('Iframe not ready, applying loading behavior');
				this.isPendingShow = true;

				if (this.config.trigger.loadingBehavior === 'wait') {
					logger.log(
						'Popup is waiting for iframe ready before becoming visible'
					);
				} else {
					// Show a loading state immediately when configured to do so.
					this.revealPopupWithLoader();
				}
			}
		});
	}

	/**
	 * Force show the popup, bypassing dismissal cookie check.
	 * Used by admin bar debug panel for testing.
	 */
	public forceShow(): void {
		this.show(true);
	}

	/**
	 * Reveal the popup with loader visible (iframe not ready yet)
	 */
	private revealPopupWithLoader(): void {
		if (!this.container) {
			logger.warn('revealPopupWithLoader() - Container is null!');
			return;
		}

		logger.log('revealPopupWithLoader() - Revealing popup with loader');

		// Apply body state via display mode strategy
		this.displayMode.applyBodyState(this.config.display);

		// Remove the staging class and add open class
		this.container.classList.remove('gp-popup--staging');

		// Force reflow before adding open class for animation
		void this.container.offsetHeight;
		this.container.classList.add('gp-popup--open');

		// Note: isOpen stays false until iframe is ready and revealPopup() is called
		// This prevents duplicate show() calls but allows the loader to be visible
		logger.log(
			'revealPopupWithLoader() - Popup visible with loader, waiting for iframe ready'
		);
	}

	/**
	 * Reveal the popup after iframe is ready
	 */
	private revealPopup(): void {
		if (!this.container || !this.dialog) {
			logger.warn('revealPopup() - Container or dialog is null!');
			return;
		}

		logger.log('revealPopup() - Revealing popup');
		this.container.hidden = false;

		// Apply body state via display mode strategy
		this.displayMode.applyBodyState(this.config.display);

		// Remove the staging class and add open class
		this.container.classList.remove('gp-popup--staging');

		// Force reflow before adding open class for animation
		void this.container.offsetHeight;
		this.container.classList.add('gp-popup--open');
		this.restoreConfiguredAnimation();

		// Call strategy hook for mode-specific post-reveal logic (e.g., banner body padding)
		// This is called AFTER adding the open class so that banner slide animation
		// and body padding animation start at the same time
		this.displayMode.onPopupRevealed?.(this.dialog);

		this.isOpen = true;
		this.isPendingShow = false;
		logger.log('revealPopup() - Popup is now open');

		// Broadcast open state
		this.broadcastState('open');

		// Track impression
		this.tracker?.trackImpression(this.config.feedId, this.config.formId);

		// Focus first field after animation
		setTimeout(() => {
			if (this.inlineContent) {
				if (this.isOpen) {
					this.inlineContent.focus();
				}
			} else {
				this.sendToIframe('focus_first_field');
			}
		}, 300);

		// Dispatch custom event
		this.dispatchEvent('gp_popup_opened', { feedId: this.config.feedId });
	}

	/** Restore the configured animation after an instant first page-load reveal. */
	private restoreConfiguredAnimation(): void {
		if (!this.container || !this.restoreAnimationAfterInitialReveal) {
			return;
		}

		// Commit the fully open, transition-free state before changing classes.
		void this.container.offsetHeight;
		this.container.classList.remove('gp-popup--none');
		this.container.classList.add(
			`gp-popup--${this.config.display.animationType}`
		);
		this.restoreAnimationAfterInitialReveal = false;
	}

	/**
	 * Hide the popup
	 */
	public hide(setDismissalCookie = true): void {
		if (
			(!this.isOpen && !this.isPendingShow) ||
			this.isClosing ||
			!this.container
		) {
			return;
		}
		this.isClosing = true;
		this.isPendingShow = false;

		// Track dismissal only if user dismissed (not auto-close after submission)
		if (setDismissalCookie) {
			this.tracker?.trackDismissal(
				this.config.feedId,
				this.config.formId
			);
			// Broadcast dismissed state
			this.broadcastState('dismissed');
		}

		this.container.classList.remove('gp-popup--open');
		this.container.classList.add('gp-popup--closing');

		// Call strategy hook for mode-specific pre-removal logic (e.g., banner body padding animation)
		// This runs in parallel with the popup closing animation
		const removingPromise = this.displayMode.onPopupRemoving?.();

		// Wait for animation to complete
		setTimeout(async () => {
			// Wait for any strategy-specific animations to complete
			if (removingPromise) {
				await removingPromise;
			}

			this.removePopup();
			this.isOpen = false;
			this.isClosing = false;

			// Set dismissal cookie if configured
			if (setDismissalCookie && this.config.behavior.respectDismissal) {
				this.setDismissalCookie();
			}

			// Dispatch custom event
			this.dispatchEvent('gp_popup_closed', {
				feedId: this.config.feedId,
			});
		}, 300);
	}

	/**
	 * Toggle popup visibility
	 */
	public toggle(): void {
		if (this.isOpen) {
			this.hide();
		} else {
			this.show();
		}
	}

	/**
	 * Destroy the popup instance
	 */
	public destroy(): void {
		this.removePopup();
		this.isOpen = false;
		this.isPendingShow = false;
		this.isClosing = false;
		if (this.inlineContent) {
			this.inlineContent.destroy();
			this.inlineContent = null;
			this.container?.remove();
			this.container = null;
			this.dialog = null;
			this.backdrop = null;
			this.closeButton = null;
		}
		this.removeEventListeners();
	}

	/**
	 * Check if iframe is ready (preloaded and form rendered)
	 */
	public getIsReady(): boolean {
		return this.isReady;
	}

	/**
	 * Preload iframe in background.
	 * Called by triggers at the appropriate time for smart preloading.
	 *
	 * Creates the full popup structure but keeps it hidden/staged.
	 * This avoids iframe reload when moving DOM elements.
	 */
	public preload(): void {
		logger.group(`preload() feedId: ${this.config.feedId}`, () => {
			// Don't preload if already preloaded or preloading disabled
			if (this.container) {
				logger.log('Popup already exists, skipping');
				return;
			}

			if (!this.config.behavior.preloadIframe) {
				logger.log('Preloading disabled in config, skipping');
				return;
			}

			logger.log('Creating popup structure for preload');
			// Broadcast preloading state
			this.broadcastState('preloading');

			// Create the full popup structure in staging mode
			// This way we don't need to move the iframe later (which causes reload)
			this.createPopup();
		});
	}

	/**
	 * Broadcast popup lifecycle state to the admin bar debug panel
	 */
	private broadcastState(state: PopupLifecycleState): void {
		broadcastPopupState({
			feedId: this.config.feedId,
			state,
			isReady: this.isReady,
			isOpen: this.isOpen,
		});
	}

	/**
	 * Sync the iframe src with the current (filtered) iframeUrl.
	 * Called at show time so filters can inject runtime values
	 * (e.g. form field values) that weren't available at preload.
	 */
	private syncIframeUrl(): void {
		if (!this.iframe) {
			return;
		}

		const filteredUrl = this.config.iframeUrl;
		if (this.iframe.src !== filteredUrl) {
			this.iframe.src = filteredUrl;
			this.isReady = false;
		}
	}

	/**
	 * Create the iframe element
	 */
	private createIframe(): HTMLIFrameElement {
		const iframe = document.createElement('iframe');
		iframe.src = this.config.iframeUrl;
		iframe.className = 'gp-popup__iframe';
		// Give the iframe a meaningful accessible name; config.title carries the
		// (filterable) popup title, with a generic fallback if it's missing.
		iframe.setAttribute(
			'title',
			this.config.title || (this.config.formId ? 'Form Popup' : 'Popup')
		);
		// Don't use loading="lazy" - we handle preloading ourselves and
		// lazy loading won't work when iframe is positioned off-screen
		iframe.setAttribute('sandbox', this.config.iframeSandbox);
		// Disable iframe scrolling - let the content wrapper handle overflow
		iframe.setAttribute('scrolling', 'no');
		// Enable transparency for older browsers/WebViews
		iframe.setAttribute('allowtransparency', 'true');
		return iframe;
	}

	/**
	 * Create the popup DOM structure
	 */
	private createPopup(): void {
		if (this.adoptInitialPopup()) {
			return;
		}
		const { display } = this.config;

		// Page-load inline popups should appear immediately once eligibility is known.
		// Restore the configured animation after the first reveal for later reopens.
		this.restoreAnimationAfterInitialReveal =
			this.config.renderMode === 'inline' &&
			this.config.trigger.type === 'page_load' &&
			display.animationType !== 'none';
		const animationType = this.restoreAnimationAfterInitialReveal
			? 'none'
			: display.animationType;

		// Container - start in staging mode (off-screen but rendered) until iframe is ready
		this.container = document.createElement('div');
		this.container.className = `gp-popup gp-popup--feed-${this.config.feedId} gp-popup--${animationType} gp-popup--staging`;

		// Add display mode classes via strategy
		this.displayMode.getDisplayModeClasses().forEach((cls) => {
			this.container!.classList.add(cls);
		});

		// Corner popups without backdrop should not block page interaction.
		// Keep only the dialog interactive via CSS.
		if (display.displayMode === 'corner' && !display.showBackdrop) {
			this.container.classList.add('gp-popup--corner-no-backdrop');
		}

		if (display.customCssClass) {
			this.container.classList.add(display.customCssClass);
		}

		if (display.transparentBackground) {
			this.container.classList.add('gp-popup--transparent');
		}

		this.container.style.setProperty(
			'--gp-popup-z-index',
			String(display.zIndex)
		);
		this.container.setAttribute('role', 'dialog');
		// Set the accessible name directly via aria-label. We can't use
		// aria-labelledby here because the popup title lives inside the iframe,
		// which is a separate document, a cross-document IDREF is invalid and
		// leaves the dialog with no accessible name.
		const accessibleName =
			this.config.title || (this.config.formId ? 'Form Popup' : 'Popup');
		this.container.setAttribute('aria-label', accessibleName);

		// Backdrop (not shown for fullscreen mode - handled by CSS)
		// For corner mode, backdrop is optional based on showBackdrop setting
		const shouldShowBackdrop =
			display.displayMode !== 'corner' || display.showBackdrop;

		if (shouldShowBackdrop) {
			this.backdrop = document.createElement('div');
			this.backdrop.className = 'gp-popup__backdrop';
			this.backdrop.style.backgroundColor = display.backdropColor;
			if (display.backdropBlur) {
				this.backdrop.style.backdropFilter = 'blur(4px)';
			}

			// Use strategy to determine if backdrop click should close popup
			if (this.displayMode.shouldCloseOnBackdropClick(display)) {
				this.backdrop.addEventListener('click', () => this.hide());
			}
		}

		// Dialog
		this.dialog = document.createElement('div');
		this.dialog.className = 'gp-popup__dialog';

		// Apply sizing via display mode strategy
		this.displayMode.applyDialogSizing?.(this.dialog, display);

		// Close button
		if (display.showCloseButton) {
			this.closeButton = document.createElement('button');
			this.closeButton.className = 'gp-popup__close';
			this.closeButton.setAttribute('type', 'button');
			this.closeButton.setAttribute('aria-label', 'Close popup');
			this.closeButton.innerHTML = `
				<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
					<line x1="18" y1="6" x2="6" y2="18"></line>
					<line x1="6" y1="6" x2="18" y2="18"></line>
				</svg>
			`;
			this.closeButton.addEventListener('click', () => this.hide());
			this.dialog.appendChild(this.closeButton);
		}

		// Content wrapper
		const content = document.createElement('div');
		content.className = 'gp-popup__content';

		const inlineRoot =
			this.config.renderMode === 'inline'
				? InlineContent.find(this.config.feedId, this.config.formId)
				: null;
		if (inlineRoot) {
			this.initializeInlineContent(inlineRoot, content);
		} else {
			// Add loading indicator (will be removed when iframe is ready).
			const loader = document.createElement('div');
			loader.className = 'gp-popup__loader';
			loader.innerHTML =
				this.config.loaderHtml ||
				'<div class="gp-popup__spinner"></div>';
			content.appendChild(loader);
			this.iframe = this.createIframe();
			content.appendChild(this.iframe);
		}

		// Assemble
		this.dialog.appendChild(content);
		if (this.backdrop) {
			this.container.appendChild(this.backdrop);
		}
		this.container.appendChild(this.dialog);
		document.body.appendChild(this.container);
		if (this.inlineContent) {
			this.broadcastState('preloaded');
			this.dispatchEvent('gp_popup_ready', {
				feedId: this.config.feedId,
			});
		}

		// Note: Don't add body scroll prevention here - it's added in revealPopup()
		// This allows preload to create the popup without affecting page scroll
	}

	/** Reuse a banner that PHP already displayed before the frontend bundle ran. */
	private adoptInitialPopup(): boolean {
		const shell = document.querySelector<HTMLElement>(
			`[data-gp-popup-initial="${this.config.feedId}"]`
		);
		if (!shell) {
			return false;
		}
		const inlineRoot = InlineContent.find(
			this.config.feedId,
			this.config.formId
		);
		const dialog = shell.querySelector<HTMLElement>('.gp-popup__dialog');
		const content = shell.querySelector<HTMLElement>('.gp-popup__content');
		if (
			this.config.renderMode !== 'inline' ||
			this.config.display.displayMode !== 'banner' ||
			!inlineRoot ||
			!dialog ||
			!content
		) {
			hideInitialPopup(this.config.feedId);
			if (inlineRoot && this.config.renderMode === 'inline') {
				inlineRoot.hidden = true;
				document.body.appendChild(inlineRoot);
			}
			shell.remove();
			return false;
		}

		this.container = shell;
		this.dialog = dialog;
		const initialShell = shell as HTMLElement & {
			gpPopupEarlyDismiss?: EventListener;
		};
		if (initialShell.gpPopupEarlyDismiss) {
			shell.removeEventListener(
				'click',
				initialShell.gpPopupEarlyDismiss
			);
			delete initialShell.gpPopupEarlyDismiss;
		}
		this.closeButton = shell.querySelector('.gp-popup__close');
		this.closeButton?.addEventListener('click', () => this.hide());
		this.displayMode.applyDialogSizing?.(dialog, this.config.display);
		this.initializeInlineContent(inlineRoot, content);
		this.broadcastState('preloaded');
		this.dispatchEvent('gp_popup_ready', { feedId: this.config.feedId });
		return true;
	}

	private initializeInlineContent(
		root: HTMLElement,
		content: HTMLElement
	): void {
		const { display } = this.config;
		this.container!.classList.add('gp-popup--inline');
		root.style.setProperty(
			'--gp-popup-inner-width',
			display.innerWidth || '100%'
		);
		this.inlineContent = new InlineContent(
			root,
			this.config.feedId,
			this.config.formId,
			{
				onRendered: () => this.onFormRendered(),
				onSubmitted: () =>
					this.onFormSubmitted({ formId: this.config.formId }),
				onPageChanged: (currentPage) =>
					this.onPageChanged({
						formId: this.config.formId,
						currentPage,
					}),
				onClose: () => this.hide(),
				onRedirect: (url) => this.onRedirect({ url }),
				onResize: () => {
					if (this.dialog) {
						this.displayMode.onPopupRevealed?.(this.dialog);
					}
				},
				isOpen: () => this.isOpen,
			},
			display.displayMode !== 'banner' &&
				(display.displayMode !== 'corner' || display.showBackdrop)
		);
		this.inlineContent.mount(content, this.dialog!);
		this.isReady = true;
	}

	/**
	 * Remove the popup from DOM
	 */
	private removePopup(): void {
		// Call strategy hook for mode-specific cleanup
		this.displayMode.onPopupRemoving?.();

		// Remove body state via display mode strategy
		this.displayMode.removeBodyState(this.config.display);

		if (this.inlineContent && this.container) {
			// Keep the initialized form and its AJAX iframe in place. Recreating or
			// moving them would lose field state and add-on event handlers.
			this.inlineContent.hide();
			this.container.hidden = true;
			this.container.classList.remove(
				'gp-popup--open',
				'gp-popup--closing'
			);
			if (this.container.hasAttribute('data-gp-popup-initial')) {
				this.container.removeAttribute('data-gp-popup-initial');
				this.container.classList.remove(
					'gp-popup--initial',
					'gp-popup--none'
				);
				this.container.classList.add(
					`gp-popup--${this.config.display.animationType}`
				);
			}
			return;
		}

		if (this.container) {
			this.container.remove();
			this.container = null;
			this.backdrop = null;
			this.dialog = null;
			this.closeButton = null;
			this.iframe = null;
			this.isReady = false;
			this.isPendingShow = false;
		}
	}

	/**
	 * Attach event listeners
	 */
	private attachEventListeners(): void {
		// Note: message listener is already attached in constructor for preload support

		if (this.config.display.closeOnEsc) {
			document.addEventListener('keydown', this.boundHandleKeydown);
		}
	}

	/**
	 * Remove event listeners
	 */
	private removeEventListeners(): void {
		window.removeEventListener('message', this.boundHandleMessage);
		document.removeEventListener('keydown', this.boundHandleKeydown);
	}

	/**
	 * Handle messages from iframe
	 */
	private handleMessage(event: MessageEvent): void {
		// A cancelled iframe can finish loading after its replacement was created.
		// Only the current iframe may advance this popup's lifecycle.
		if (
			this.isClosing ||
			!this.iframe ||
			event.source !== this.iframe.contentWindow
		) {
			return;
		}
		const data = event.data as IframeMessage;

		// Validate message source
		if (!data || data.source !== 'gp-popups') {
			return;
		}

		// Validate feed ID
		if (data.feedId !== this.config.feedId) {
			return;
		}

		switch (data.type) {
			case 'ready':
				this.onIframeReady(data.data as { height: number });
				break;

			case 'resize':
				this.onIframeResize(data.data as { height: number });
				break;

			case 'form_submitted':
				this.onFormSubmitted(data.data as { formId: number });
				break;

			case 'form_rendered':
				this.onFormRendered();
				break;

			case 'page_changed':
				this.onPageChanged(
					data.data as { formId: number; currentPage: number }
				);
				break;

			case 'close_popup':
				this.hide();
				break;

			case 'redirect':
				this.onRedirect(data.data as { url: string });
				break;
		}
	}

	/**
	 * Handle keydown events
	 */
	private handleKeydown(event: KeyboardEvent): void {
		if (event.key === 'Escape' && (this.isOpen || this.isPendingShow)) {
			this.hide();
		}
	}

	/**
	 * Handle iframe ready event
	 */
	private onIframeReady(data: { height: number }): void {
		logger.log('onIframeReady() called, height:', data.height);
		this.isReady = true;

		// Broadcast preloaded state (iframe is ready)
		this.broadcastState('preloaded');

		// Remove loader
		const loader = this.container?.querySelector('.gp-popup__loader');
		if (loader) {
			loader.remove();
		}

		// Auto-resize if height is 'auto' (but never for fullscreen mode)
		if (this.config.display.displayMode !== 'fullscreen') {
			if (this.config.display.popupHeight === 'auto') {
				this.setIframeHeight(data.height);
			}
		}

		// If we were waiting to show the popup, reveal it now
		if (this.isPendingShow) {
			logger.log(
				'onIframeReady() - Popup was pending show, revealing now'
			);
			this.revealPopup();
		}

		this.dispatchEvent('gp_popup_ready', { feedId: this.config.feedId });
	}

	/**
	 * Handle iframe resize event
	 */
	private onIframeResize(data: { height: number }): void {
		// Auto-resize if height is 'auto' (but never for fullscreen mode)
		if (this.config.display.displayMode !== 'fullscreen') {
			if (this.config.display.popupHeight === 'auto') {
				this.setIframeHeight(data.height);
			}
		}
	}

	/**
	 * Handle form submitted event
	 */
	private onFormSubmitted(data: { formId: number }): void {
		// Broadcast submitted state
		this.broadcastState('submitted');

		this.dispatchEvent('gp_popup_submitted', {
			feedId: this.config.feedId,
			formId: data.formId,
		});

		// Set dismissal cookie so submitted popups are not shown again
		if (this.config.behavior.respectDismissal) {
			this.setDismissalCookie();
		}

		// Auto-close if configured
		if (this.config.behavior.autoCloseOnSubmission) {
			const { autoCloseDelay, autoCloseDelayUnit } = this.config.behavior;
			const delayMs =
				autoCloseDelayUnit === 's'
					? autoCloseDelay * 1000
					: autoCloseDelay;
			setTimeout(() => this.hide(false), delayMs);
		}
	}

	/**
	 * Handle form rendered event
	 */
	private onFormRendered(): void {
		this.dispatchEvent('gp_popup_form_rendered', {
			feedId: this.config.feedId,
		});
	}

	/**
	 * Handle page changed event (multi-page forms)
	 */
	private onPageChanged(data: { formId: number; currentPage: number }): void {
		this.dispatchEvent('gp_popup_page_changed', {
			feedId: this.config.feedId,
			formId: data.formId,
			currentPage: data.currentPage,
		});
	}

	/**
	 * Handle redirect confirmation from iframe.
	 * When a form has a Page or Redirect confirmation type, the iframe sends
	 * a redirect message instead of navigating. We redirect the parent window.
	 */
	private onRedirect(data: { url: string }): void {
		logger.log('Redirect confirmation received:', data.url);

		// Broadcast submitted state (uses sendBeacon for reliability during navigation)
		this.broadcastState('submitted');

		// Set dismissal cookie so submitted popups are not shown again
		if (this.config.behavior.respectDismissal) {
			this.setDismissalCookie();
		}

		// Fire event for developers to hook into
		// Developers can call event.preventDefault() to stop the redirect
		const event = this.dispatchEvent(
			'gp_popup_redirect',
			{
				feedId: this.config.feedId,
				url: data.url,
			},
			true
		);

		// Allow developers to prevent redirect via event.preventDefault()
		if (event.defaultPrevented) {
			logger.log('Redirect prevented by event handler');
			return;
		}

		// Small delay to ensure analytics beacon is sent
		// The iframe already shows a spinner in the confirmation message
		setTimeout(() => {
			// Perform redirect in parent window
			window.location.href = data.url;
		}, 100);
	}

	/**
	 * Set iframe height
	 */
	private setIframeHeight(height: number): void {
		if (this.iframe) {
			this.iframe.style.height = `${height}px`;
		}
	}

	/**
	 * Send message to iframe
	 */
	private sendToIframe(
		type: ParentMessage['type'],
		data?: Record<string, unknown>
	): void {
		if (this.iframe?.contentWindow) {
			const message: ParentMessage = {
				source: 'gp-popups-parent',
				type,
				data,
			};
			this.iframe.contentWindow.postMessage(message, '*');
		}
	}

	/**
	 * Dispatch custom event
	 */
	private dispatchEvent(
		name: string,
		detail: Record<string, unknown>,
		cancelable = false
	): CustomEvent {
		const event = new CustomEvent(name, {
			detail,
			bubbles: true,
			cancelable,
		});

		document.dispatchEvent(event);

		return event;
	}

	/**
	 * Check if popup was previously dismissed
	 */
	private wasDismissed(): boolean {
		return getCookie(this.config.behavior.cookieName) === '1';
	}

	/**
	 * Set dismissal cookie
	 */
	private setDismissalCookie(): void {
		const days = this.config.behavior.cookieDuration;
		setCookie(this.config.behavior.cookieName, '1', days);
	}
}
